import get from 'lodash/get';
import { combineReducers } from 'redux';
import { createReducer } from 'redux-act';
import { createSelector, createSelectorCreator, lruMemoize } from 'reselect';
import shallowequal from 'shallowequal';
import { createResource } from '@gandi/resource/redux';
import { baseUrl as moduleBaseUrl, hashString, statePath as moduleStatePath } from '../utils';
export const EVENTS_STATUS_BILLING = 'billing';
export const EVENTS_STATUS_CANCELLED = 'cancelled';
export const EVENTS_STATUS_DONE = 'done';
export const EVENTS_STATUS_ERROR = 'error';
export const EVENTS_STATUS_RUNNING = 'running';
export const EVENTS_STATUS_WAITING = 'waiting';
export const EVENTS_CATEGORY_INSTANCE = 'instance';
export const EVENTS_CATEGORY_NAME = 'name';
export const EVENTS_CATEGORY_PASSWORD = 'password';
export const EVENTS_CATEGORY_SNAPSHOT = 'snapshot';
export const EVENTS_CATEGORY_SSHKEY = 'sshkey';
export const EVENTS_CATEGORY_RENEW = 'renew';
export const EVENTS_CATEGORY_AUTORENEW = 'autorenew';
export const EVENTS_CATEGORY_CONSOLE = 'console';
export const EVENTS_CATEGORY_VHOST = 'vhost';
export const EVENTS_CATEGORY_DATABASE = 'database';
export const EVENTS_CATEGORY_DISK = 'disk';
export const EVENTS_TYPE_CREATE = 'create';
export const EVENTS_TYPE_RESTART = 'restart';
export const EVENTS_TYPE_DELETE = 'delete';
export const EVENTS_TYPE_UPDATE = 'update';
export const EVENTS_TYPE_ACTIVATE = 'activate';
export const EVENTS_TYPE_UPGRADE = 'upgrade';
export const EVENTS_TYPE_SCALE = 'scale';
export const statePath = `${moduleStatePath}.events`;
export const baseUrl = `${moduleBaseUrl}/instances`;
export const DEFAULT_PAGE = 1;
export const DEFAULT_PER_PAGE = 50;
export const DEFAULT_SORT_BY = 'date_created';
export const defaultParams = {
  page: DEFAULT_PAGE,
  per_page: DEFAULT_PER_PAGE,
  sort_by: DEFAULT_SORT_BY
};
export const computeLabel = params => hashString(JSON.stringify({
  ...defaultParams,
  ...params
}));
export const resourceName = 'resources-simplehosting.events';
export const resource = createResource(resourceName);
export const fetch = (instanceId, params = {}, options = {}) => dispatch => {
  const mergedParams = {
    ...defaultParams,
    ...params
  };
  const label = computeLabel({
    ...mergedParams,
    id: instanceId
  });
  return dispatch(resource.fetch(`${baseUrl}/${instanceId}/events`, {
    id: instanceId,
    label,
    params: {
      ...mergedParams
    },
    on200: response => response,
    not200: response => Promise.reject(response),
    ...options
  }));
};
export const invalidate = () => dispatch => dispatch(resource.invalidate());
export const initialState = {
  summaries: {}
};
export const handleFetchSuccess = (state, payload) => ({
  ...state,
  [payload.id]: {
    ...state[payload.id],
    data: payload.response.data.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {
      ...state.data
    }),
    [payload.label]: {
      ids: payload.response.data.map(item => item.id),
      totalCount: parseInt(payload.response.headers.get('x-total-count'), 10)
    }
  }
});
export const summariesReducer = createReducer({
  [resource.fetch.success.getType()]: handleFetchSuccess
}, initialState.summaries);
export const reducer = combineReducers({
  summaries: summariesReducer
});
const selectRawState = state => get(state, statePath, {});
export const selectRawSummaries = createSelector([selectRawState, (_, props) => props.id], (raw, instanceId) => raw.summaries[instanceId]);
export const selectSummariesData = createSelector([selectRawSummaries], raw => raw ? raw.data : undefined);
const createShallowEqualSelector = createSelectorCreator(lruMemoize, shallowequal);
export const selectComputedLabel = createShallowEqualSelector([selectRawSummaries, (_, props) => computeLabel(props)], (raw, label) => label);
export const selectSummariesMeta = createSelector([selectRawSummaries, selectComputedLabel], (raw, label) => raw ? raw[label] : undefined);
export const selectSummariesTotalCount = createSelector([selectSummariesMeta], meta => meta ? meta.totalCount : undefined);
export const selectSummaries = createSelector([selectSummariesData, selectSummariesMeta], (data, meta) => meta ? meta.ids.map(id => get(data, id)).filter(Boolean) : undefined);
export const selectIsFetching = (state, props) => resource.selectIsFetching(state, selectComputedLabel(state, props));
export const selectIsLoaded = (state, props) => resource.selectIsLoaded(state, selectComputedLabel(state, props));
export const selectIsFailure = (state, props) => resource.selectIsFailure(state, selectComputedLabel(state, props));
export const selectIsForbidden = (state, props) => resource.selectIsFailure(state, {
  label: selectComputedLabel(state, props),
  status: 403
});
export const selectIsToRefetch = (state, props) => resource.selectIsToRefetch(state, selectComputedLabel(state, props));